export default function RetainerBenefits() {
  const benefits = [
    {
      icon: 'ri-user-add-line',
      title: 'New Staff Onboarding',
      description: 'Seamless medical training integration for new team members. We provide immediate training sessions for newly hired staff, ensuring they meet your established medical readiness standards from day one.',
      features: [
        'Priority scheduling for new hires',
        'Accelerated certification programs',
        'Integration with existing team protocols',
        'Customized onboarding materials'
      ]
    },
    {
      icon: 'ri-refresh-line',
      title: 'Memory Refresher Sessions',
      description: 'Regular skill maintenance and knowledge updates to keep your team sharp. Periodic refresher training ensures critical medical response skills remain fresh and effective when needed most.',
      features: [
        'Quarterly skill assessment reviews',
        'Updated protocol training',
        'Hands-on practice sessions',
        'Latest medical guidelines integration'
      ]
    },
    {
      icon: 'ri-24-hours-line',
      title: 'Priority Response Access',
      description: 'Immediate consultation and support when you need it. Retainer clients receive priority access to our medical training experts for urgent questions, protocol clarifications, or emergency guidance.',
      features: [
        '24/7 consultation hotline',
        'Rapid response to training needs',
        'Emergency protocol guidance',
        'Direct expert access'
      ]
    },
    {
      icon: 'ri-file-text-line',
      title: 'Compliance Documentation',
      description: 'Comprehensive record-keeping and certification management. We maintain detailed training records, certification tracking, and compliance documentation for your entire team.',
      features: [
        'Digital certification management',
        'Automated renewal reminders',
        'Audit-ready documentation',
        'Regulatory compliance tracking'
      ]
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Performance Analytics',
      description: 'Track team readiness and identify improvement areas. Detailed analytics help you understand your team\'s medical response capabilities and optimize training investments.',
      features: [
        'Team competency dashboards',
        'Skill gap analysis',
        'Training effectiveness metrics',
        'Customized reporting'
      ]
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost Predictability',
      description: 'Fixed monthly investment with unlimited training value. Retainer agreements provide budget certainty while ensuring your team always has access to world-class medical training.',
      features: [
        'Predictable monthly costs',
        'No per-session fees',
        'Unlimited refresher sessions',
        'Volume training discounts'
      ]
    }
  ];

  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#707070] font-light mb-4">
            Ongoing Excellence
          </div>
          <h2 className="text-[40px] sm:text-[48px] md:text-[56px] font-bold text-[#F5F5F5] mb-6 font-serif">
            Retainer Benefits
          </h2>
          <p className="text-[16px] text-[#9A9A9A] max-w-[800px] mx-auto leading-relaxed">
            Our retainer programs ensure your team maintains peak medical readiness through continuous support, regular refreshers, and seamless new staff integration.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#D4C4B0] transition-all duration-300 group"
            >
              <div className="w-16 h-16 bg-[#1A1815] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D4C4B0] transition-all duration-300">
                <i className={`${benefit.icon} text-[32px] text-[#D4C4B0] group-hover:text-black transition-all duration-300`}></i>
              </div>
              
              <h3 className="text-[22px] font-bold text-[#F5F5F5] mb-4">
                {benefit.title}
              </h3>
              
              <p className="text-[14px] text-[#9A9A9A] leading-relaxed mb-6">
                {benefit.description}
              </p>

              <div className="space-y-3">
                {benefit.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-[#D4C4B0] text-[16px] mt-0.5 flex-shrink-0"></i>
                    <span className="text-[13px] text-[#B0B0B0]">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#1A1815] to-[#0F0F0F] border border-[#D4C4B0]/20 rounded-2xl p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#D4C4B0]/10 border border-[#D4C4B0]/30 rounded-full mb-6">
                <span className="text-[12px] text-[#D4C4B0] font-semibold uppercase tracking-wider">
                  Exclusive Retainer Advantage
                </span>
              </div>
              
              <h3 className="text-[32px] md:text-[40px] font-bold text-[#F5F5F5] mb-6 font-serif">
                Continuous Medical Readiness
              </h3>
              
              <p className="text-[15px] text-[#B0B0B0] leading-relaxed mb-8">
                Medical emergencies don't wait for convenient timing. Our retainer programs ensure your team is always prepared, with ongoing training that adapts to staff changes, skill degradation, and evolving medical protocols.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4C4B0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-[24px] text-[#D4C4B0]"></i>
                  </div>
                  <div>
                    <h4 className="text-[16px] font-semibold text-[#F5F5F5] mb-1">
                      Zero Training Gaps
                    </h4>
                    <p className="text-[13px] text-[#909090]">
                      New staff trained immediately, existing staff refreshed regularly
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4C4B0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-[24px] text-[#D4C4B0]"></i>
                  </div>
                  <div>
                    <h4 className="text-[16px] font-semibold text-[#F5F5F5] mb-1">
                      Flexible Scheduling
                    </h4>
                    <p className="text-[13px] text-[#909090]">
                      Training sessions scheduled around your operational needs
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4C4B0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-[24px] text-[#D4C4B0]"></i>
                  </div>
                  <div>
                    <h4 className="text-[16px] font-semibold text-[#F5F5F5] mb-1">
                      Dedicated Training Team
                    </h4>
                    <p className="text-[13px] text-[#909090]">
                      Consistent instructors who understand your specific environment
                    </p>
                  </div>
                </div>
              </div>

              <a 
                href="#contact" 
                className="inline-block px-8 py-4 bg-[#D4C4B0] text-black text-[14px] font-semibold text-center rounded-lg hover:bg-[#C4B4A0] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Discuss Retainer Options
              </a>
            </div>

            <div className="relative">
              <div className="bg-[#0D0D0D] border border-[#222222] rounded-2xl p-8">
                <h4 className="text-[20px] font-bold text-[#F5F5F5] mb-6 flex items-center gap-3">
                  <i className="ri-calendar-check-line text-[#D4C4B0]"></i>
                  Typical Retainer Timeline
                </h4>
                
                <div className="space-y-6">
                  <div className="relative pl-8 pb-6 border-l-2 border-[#222222]">
                    <div className="absolute left-[-9px] top-0 w-4 h-4 bg-[#D4C4B0] rounded-full"></div>
                    <div className="text-[11px] text-[#707070] uppercase tracking-wider mb-2">Month 1</div>
                    <h5 className="text-[15px] font-semibold text-[#F5F5F5] mb-1">Initial Assessment</h5>
                    <p className="text-[13px] text-[#909090]">Team evaluation and customized program design</p>
                  </div>

                  <div className="relative pl-8 pb-6 border-l-2 border-[#222222]">
                    <div className="absolute left-[-9px] top-0 w-4 h-4 bg-[#D4C4B0] rounded-full"></div>
                    <div className="text-[11px] text-[#707070] uppercase tracking-wider mb-2">Months 2-3</div>
                    <h5 className="text-[15px] font-semibold text-[#F5F5F5] mb-1">Core Training</h5>
                    <p className="text-[13px] text-[#909090]">Comprehensive certification for all team members</p>
                  </div>

                  <div className="relative pl-8 pb-6 border-l-2 border-[#222222]">
                    <div className="absolute left-[-9px] top-0 w-4 h-4 bg-[#D4C4B0] rounded-full"></div>
                    <div className="text-[11px] text-[#707070] uppercase tracking-wider mb-2">Ongoing</div>
                    <h5 className="text-[15px] font-semibold text-[#F5F5F5] mb-1">Quarterly Refreshers</h5>
                    <p className="text-[13px] text-[#909090]">Regular skill maintenance and new staff onboarding</p>
                  </div>

                  <div className="relative pl-8">
                    <div className="absolute left-[-9px] top-0 w-4 h-4 bg-[#D4C4B0] rounded-full"></div>
                    <div className="text-[11px] text-[#707070] uppercase tracking-wider mb-2">Annual</div>
                    <h5 className="text-[15px] font-semibold text-[#F5F5F5] mb-1">Recertification</h5>
                    <p className="text-[13px] text-[#909090]">Full team recertification and program updates</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-[#D4C4B0]/5 border border-[#D4C4B0]/20 rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <i className="ri-information-line text-[32px] text-[#D4C4B0]"></i>
                  <div>
                    <h5 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">
                      Flexible Agreements
                    </h5>
                    <p className="text-[12px] text-[#909090]">
                      Retainer terms customized to your team size and training frequency needs
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
